import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear, scaleThreshold } from "d3-scale";
import { select, selectAll } from "d3-selection";
import { min, max, extent, ascending } from "d3-array";
import { geoPath, geoAlbersUsa, geoAlbers, geoConicEqualArea, fitSize} from "d3-geo";
// import { geoAlbers, geoConicEqualArea  } from "d3-geo-projection";

let topojson = require("topojson-client");
import Tooltip from './tooltipFactory.js';

export default class makeChart {

  constructor(opts) {
    Object.assign(this, opts);
    this.aspectHeight = opts.aspectHeight ? opts.aspectHeight : 0.65;

    this.stateData = this.data.filter(f=> f.geo_type == "state")
    this.stateNames = this.stateData.map(d=> d.location_name)
    this.cityData = this.data.filter(f=> f.geo_type != "state" && f.geo_type != "federal" && f.location_geo != "Puerto Rico")
    this.bottomData = this.data.filter(f=> f.geo_type == "federal")

    topojson.feature(this.stateTopo, this.stateTopo.objects.states_20m_2017).features.forEach(feature=> {
      feature.properties.selected = false
      this.stateData.forEach(state=> {
        if (feature.properties.NAME == state.location_geo) {
          feature.properties.selected = true
          feature.properties.case = state.case
          feature.properties.location_name = state.location_name
          feature.properties.date = state.date
          feature.properties.legal_claims = state.legal_claims
          feature.properties.type = state.type
          feature.properties.defendants = state.defendants
        }
      })
    })

    this.theTooltip = new Tooltip({
      'parent' : '.map-container',
      'formats': {
        "none" : d=> d,
        "val": d=> d !== null ? `${parseInt(d)}` : "N/A"
      }
    });

    this.appendElements();
    this.update();
  }

  update() {
    this._setDimensions();
    this._setScales();
    this.render();
  }

  _setDimensions() {
    this.size = window.innerWidth <= 420 ? "mobile" : window.innerWidth > 421 && window.innerWidth <= 599 ? "tablet" : "desktop"; // Chris made this, handles map alignment in ways I don't want to mess with
    this.isMobile = window.innerWidth < 620 ? true : false; // Needed different breakpoints for state label positioning

    // define width, height and margin
    this.margin = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };

    this.width = this.element.offsetWidth - this.margin.left - this.margin.right;
    this.height = this.element.offsetWidth * this.aspectHeight - this.margin.top - this.margin.bottom;
  }

  _setScales() {
    let size = this.width *1.35;
    this.projection = geoAlbersUsaPr()
      .scale(size).translate([size*0.375, size*0.2346153846])

    this.path = geoPath()
      .projection(this.projection);
  }

  appendElements() {
    this.svg = select(this.element).append("svg");

    this.interactionRect = this.svg.append("rect").attr("class", "interaction-rect");

    this.plot = this.svg.append("g").attr("class", "chart-g");

    this.stateBorders = this.plot.append("path")
    .attr("class", "state-borders")

    this.states = this.plot.append("g")
      .attr("class", "state-g")
      .selectAll(".state")
      .data(topojson.feature(this.stateTopo, this.stateTopo.objects.states_20m_2017).features)
      .enter().append("path")
      .filter(d=> d.properties.STUSPS != "AS" && d.properties.STUSPS != "GU" && d.properties.STUSPS != "VI" && d.properties.STUSPS != "MP")
      .attr("class", d=> {
        let selected = d.properties.selected ? "selected" : "";
        return `${d.properties.STUSPS} ${selected} state`
      })

    this.cityContainer = this.plot.append("g").attr("class", "city-container")

    this.cities = this.cityContainer.selectAll("circle.city")
      .data(this.cityData)
      .enter()
      .append("circle")
      .attr("class", d=> `city ${slugify(d.location_geo)}`)

    this.bottomCasesContainer = select(".bottom-cases")

    this.bottomCasesRow = this.bottomCasesContainer.selectAll(".bottom-case")
      .data(this.bottomData)
      .enter()
      .append("div")
      .attr("class", d=> `bottom-row ${slugify(d.location_geo)}`)

    this.bottomDot = this.bottomCasesRow.append("div")
      .attr("class", "bottom-dot")

    this.bottomName = this.bottomCasesRow.append("div")
      .attr("class", "bottom-name")
      .text(d=> d.location_geo)
  }

  render() {
    this.svg.attr("width", this.width + this.margin.left + this.margin.right).attr("height", this.height + this.margin.top + this.margin.bottom);

    this.interactionRect
      .attr("x", -this.margin.left)
      .attr("y", -this.margin.top)
      .attr("height", this.margin.top + this.margin.bottom + this.height)
      .attr("width", this.margin.left + this.margin.right + this.width)
      .attr("fill", "rgba(255,255,255,0)")
      .on("pointerdown", (event, d) =>{
        selectAll(".city").classed("is-active", false);
        selectAll(".state").classed("is-active", false);
        selectAll(".bottom-row").classed("is-active", false);
        select(".chart-container").classed('tooltip-active', false);
        this.theTooltip.deposition()
      })
    
    this.plot.attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    this.stateBorders
      .attr("d", this.path(topojson.mesh(this.stateTopo, this.stateTopo.objects.states_20m_2017, function (a, b) {
          return a !== b;
      })));

    this.states
      .attr("d", this.path)
      .on('pointerover', (event,d) =>{
        if (!d.properties.selected) {
          return
        }
        selectAll(".state").classed("is-active", false);
        selectAll(".city").classed("is-active", false);
        select(".tt-row.legal-claims").classed("hide", false)
        let stateClass = `.state.${d.properties.STUSPS}`;
        select(`${stateClass}`).classed('is-active',true).raise();
        select(".chart-container").classed('tooltip-active', true);

        if (d.properties.type.includes("constitutational")) {
          select(".tt-row.legal-claims").classed("hide", true)
        }

        let bbox = document.querySelector(`${stateClass}`).getBoundingClientRect();

        let coords = [
          bbox.x + bbox.width - bbox.width/2,
          bbox.y
        ];

        let w = this.width + this.margin.left + this.margin.right;
        let h = this.height + this.margin.top + this.margin.bottom;

        this.theTooltip.position(d.properties,coords,[w,h])
      })
      .on('pointerleave', (event, d) =>{
        if (!this.isMobile) {
          selectAll(".state").classed("is-active", false);
          select(".chart-container").classed('tooltip-active', false);
          select(".tt-row.legal-claims").classed("hide", false)
          this.theTooltip.deposition()
        }
      })

    this.cities
      .attr("r", this.isMobile ? 5 : 7)
      .attr("transform", d=> {
        return `translate(${this.projection([d.Longitude,d.Latitude])[0]},${this.projection([d.Longitude,d.Latitude])[1]})`
      })
      .on('pointerenter', (event,d) =>{
        selectAll(".city").classed("is-active", false);
        selectAll(".state").classed("is-active", false);
        select(".tt-row.legal-claims").classed("hide", false)
        let cityClass = `.city.${slugify(d.location_geo)}`;
        select(`${cityClass}`).classed('is-active',true).raise();
        select(".chart-container").classed('tooltip-active', true);

        if (d.type.includes("constitutational")) {
          select(".tt-row.legal-claims").classed("hide", true)
        }

        let bbox = document.querySelector(`${cityClass}`).getBoundingClientRect();

        let coords = [
          bbox.x + bbox.width - bbox.width/2,
          bbox.y
        ];

        let w = this.width + this.margin.left + this.margin.right;
        let h = this.height + this.margin.top + this.margin.bottom;

        this.theTooltip.position(d,coords,[w,h])
      })
      .on('pointerleave', (event, d) =>{
        if (!this.isMobile) {
          selectAll(".city").classed("is-active", false);
          select(".chart-container").classed('tooltip-active', false);
          select(".tt-row.legal-claims").classed("hide", false)
          this.theTooltip.deposition()
        }
      })

    this.bottomCasesRow
      .on('pointerenter', (event,d) =>{
        selectAll(".state").classed("is-active", false);
        selectAll(".city").classed("is-active", false);
        select(".tt-row.legal-claims").classed("hide", false)
        let bottomClass = `.bottom-row.${slugify(d.location_geo)}`;
        select(`${bottomClass}`).classed('is-active', true)
        select(".chart-container").classed('tooltip-active', true);

        if (d.type.includes("constitutational")) {
          select(".tt-row.legal-claims").classed("hide", true)
        }

        let bbox = document.querySelector(`${bottomClass}`).getBoundingClientRect();

        let coords = [
          bbox.x + bbox.width - bbox.width/2,
          bbox.y
        ];

        let w = this.width + this.margin.left + this.margin.right;
        let h = this.height + this.margin.top + this.margin.bottom;

        this.theTooltip.position(d,coords,[w,h])
      })
      .on('pointerleave', (event, d) =>{
        if (!this.isMobile) {
          selectAll(`.bottom-row`).classed('is-active', false)
          select(".chart-container").classed('tooltip-active', false);
          select(".tt-row.legal-claims").classed("hide", false)
          this.theTooltip.deposition()
        }
      })

    select(".tooltip-close")
      .on("pointerdown", (event, d) =>{
        selectAll(".city").classed("is-active", false);
        selectAll(".state").classed("is-active", false);
        selectAll(".bottom-row").classed("is-active", false);
        select(".chart-container").classed('tooltip-active', false);
        this.theTooltip.deposition()
      })

    
    select(".interaction-prompt").text(this.isMobile ? "Tap" : "Hover")
    selectAll(".selected").raise()
  }

}

function slugify(text) {
  return text.toString().toLowerCase()
  .replace(/\s+/g, '-')           // Replace spaces with -
  .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
  .replace(/\-\-+/g, '-')         // Replace multiple - with single -
  .replace(/^-+/, '')             // Trim - from start of text
  .replace(/-+$/, '');            // Trim - from end of text
}

function formatNumber(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function geoAlbersUsaPr() {
  let cache,
      cacheStream,
      lower48 = geoAlbers(), lower48Point,
      alaska = geoConicEqualArea().rotate([154, 0]).center([-2, 58.5]).parallels([55, 65]),
      alaskaPoint,
      hawaii = geoConicEqualArea().rotate([157, 0]).center([-3, 19.9]).parallels([8, 18]),
      hawaiiPoint,
      puertoRico = geoConicEqualArea().rotate([66, 0]).center([0, 18]).parallels([8, 18]),
      puertoRicoPoint,
      point,
      pointStream = {point: function(x, y) { point = [x, y]; }};

  function albersUsa(coordinates) {
    let x = coordinates[0], y = coordinates[1];
    return point = null,
        (lower48Point.point(x, y), point)
        || (alaskaPoint.point(x, y), point)
        || (hawaiiPoint.point(x, y), point)
        || (puertoRicoPoint.point(x, y), point);
  }

  albersUsa.invert = function(coordinates) {
    let k = lower48.scale(),
        t = lower48.translate(),
        x = (coordinates[0] - t[0]) / k,
        y = (coordinates[1] - t[1]) / k;
    return (y >= 0.120 && y < 0.234 && x >= -0.425 && x < -0.214 ? alaska
        : y >= 0.166 && y < 0.234 && x >= -0.214 && x < -0.115 ? hawaii
        : y >= 0.204 && y < 0.234 && x >= 0.320 && x < 0.380 ? puertoRico
        : lower48).invert(coordinates);
  };

  albersUsa.stream = function(stream) {
    return cache && cacheStream === stream ? cache : cache = multiplex([lower48.stream(cacheStream = stream), alaska.stream(stream), hawaii.stream(stream), puertoRico.stream(stream)]);
  };

  albersUsa.precision = function(_) {
    if (!arguments.length) return lower48.precision();
    lower48.precision(_), alaska.precision(_), hawaii.precision(_), puertoRico.precision(_);
    return reset();
  };

  albersUsa.scale = function(_) {
    if (!arguments.length) return lower48.scale();
    lower48.scale(_), alaska.scale(_ * 0.35), hawaii.scale(_), puertoRico.scale(_);
    return albersUsa.translate(lower48.translate());
  };

  albersUsa.translate = function(_) {
    if (!arguments.length) return lower48.translate();
    let k = lower48.scale(), x = +_[0], y = +_[1];

    lower48Point = lower48
        .translate(_)
        .clipExtent([[x - 0.455 * k, y - 0.238 * k], [x + 0.455 * k, y + 0.238 * k]])
        .stream(pointStream);

    alaskaPoint = alaska
        .translate([x - 0.307 * k, y + 0.201 * k])
        .clipExtent([[x - 0.425 * k + epsilon, y + 0.120 * k + epsilon], [x - 0.214 * k - epsilon, y + 0.234 * k - epsilon]])
        .stream(pointStream);

    hawaiiPoint = hawaii
        .translate([x - 0.205 * k, y + 0.212 * k])
        .clipExtent([[x - 0.214 * k + epsilon, y + 0.166 * k + epsilon], [x - 0.115 * k - epsilon, y + 0.234 * k - epsilon]])
        .stream(pointStream);

    puertoRicoPoint = puertoRico
        .translate([x + 0.350 * k, y + 0.224 * k])
        .clipExtent([[x + 0.320 * k, y + 0.204 * k], [x + 0.380 * k, y + 0.234 * k]])
        .stream(pointStream).point;

    return reset();
  };

  function reset() {
    cache = cacheStream = null;
    return albersUsa;
  }

  return albersUsa.scale(1070);
}

function multiplex(streams) {
  const n = streams.length;
  return {
    point(x, y) { for (const s of streams) s.point(x, y); },
    sphere() { for (const s of streams) s.sphere(); },
    lineStart() { for (const s of streams) s.lineStart(); },
    lineEnd() { for (const s of streams) s.lineEnd(); },
    polygonStart() { for (const s of streams) s.polygonStart(); },
    polygonEnd() { for (const s of streams) s.polygonEnd(); }
  };
}

let epsilon = 1e-6